import React, { useEffect, useMemo, useRef, useState } from 'react';
import { serverUrl } from '../socket';
import { useI18n } from '../useI18n.js';
import { Avatar } from './Avatar';
import { Confetti } from './Confetti';
import { playLose } from '../soundManager';
import { computeTickDodge } from '../tickLayout';

const VOL_KEY = 'tgr_mashup_vol';

function cleanTitle(name) {
  if (!name) return '';
  return String(name).replace(/\.(mp3|wav|ogg|m4a|flac)$/i, '').replace(/[_]+/g, ' ').trim();
}

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

// Recherche fuzzy simple sur les titres : tous les termes doivent matcher ; on
// privilegie les matchs en debut de nom. Retourne les `limit` meilleurs.
function fuzzyRank(pool, q, limit = 30) {
  const nq = norm(q);
  if (!nq) return pool.slice(0, limit);
  const terms = nq.split(' ').filter(Boolean);
  const scored = [];
  for (const name of pool) {
    const nn = norm(cleanTitle(name));
    let score = 0, ok = true;
    for (const term of terms) {
      const idx = nn.indexOf(term);
      if (idx < 0) { ok = false; break; }
      score += (idx === 0 ? 4 : 1) + Math.max(0, 4 - idx * 0.1) + term.length * 0.1;
    }
    if (ok) scored.push({ name, score });
  }
  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.slice(0, limit).map((s) => s.name);
}

// Avatar d'auteur (image directe cote Terraguessr, 404 -> initiales).
function AuthorAvatar({ url, label, size = 40 }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [url]);
  if (url && !err) {
    return <img src={serverUrl + url} alt="" className="guess-img" onError={() => setErr(true)} />;
  }
  return <Avatar url={null} label={label} className="guess-img" size={size} />;
}

/**
 * Ecran des modes AUDIO. mode 'mashup' = deviner l'auteur ("A qui le son ?"),
 * mode 'blindtest' = deviner le titre. Jauge synchro serveur, crans, flip, etc.
 */
export function MashupRound({
  mode = 'mashup', phase, clipUrl, clipDurationSec, clipSync, clipProgress = 0,
  mashupName, mashupAuthor, correctAnswer,
  searchPool = [], maxAttempts = 2,
  guessOptions = [], guessNames = {}, guessAvatars = {},
  hasGuessed, selectedGuess, onGuess,
  guessTicks = [], proposals = {}, myPseudo,
}) {
  const { t } = useI18n();
  const isTitle = mode === 'blindtest';
  const [query, setQuery] = useState('');
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const cpRef = useRef(0);
  const localStartRef = useRef(0); // depart local : horloge de secours si la sync serveur manque
  const [elapsed, setElapsed] = useState(0);
  const [preStart, setPreStart] = useState(true);
  const [needGesture, setNeedGesture] = useState(false);
  const [namesUp, setNamesUp] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem(VOL_KEY));
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.8;
  });

  const reveal = phase === 'result' || phase === 'transition';
  const src = clipUrl ? serverUrl + clipUrl : null;
  const clipMs = Math.max(1000, (Number(clipDurationSec) || 15) * 1000);
  const offset = clipSync ? (clipSync.serverNow - clipSync.recvAt) : 0;
  const startAt = clipSync?.startAt || 0;
  const srvNow = () => Date.now() + offset;

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    setPreStart(true);
    el.volume = volume;
    try { el.currentTime = 0; } catch { /* ignore */ }
    let armed = false;
    const onGesture = () => { el.play().then(() => { setNeedGesture(false); disarm(); }).catch(() => {}); };
    const arm = () => { if (armed) return; armed = true; ['pointerdown', 'touchstart', 'keydown'].forEach(ev => window.addEventListener(ev, onGesture)); };
    const disarm = () => { armed = false; ['pointerdown', 'touchstart', 'keydown'].forEach(ev => window.removeEventListener(ev, onGesture)); };
    const start = () => {
      setPreStart(false);
      try { el.currentTime = Math.max(0, (srvNow() - startAt) / 1000); } catch { /* ignore */ }
      const p = el.play();
      if (p && p.catch) p.then(() => setNeedGesture(false)).catch(() => { setNeedGesture(true); arm(); });
    };
    const delay = startAt ? Math.max(0, startAt - srvNow()) : 0;
    const to = setTimeout(start, delay);
    return () => { clearTimeout(to); disarm(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, startAt]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    localStorage.setItem(VOL_KEY, String(volume));
  }, [volume]);

  // Blindtest : reset des essais + recherche a chaque nouvelle manche.
  useEffect(() => { setQuery(''); setAttemptsUsed(0); }, [clipUrl]);
  const results = useMemo(() => (isTitle ? fuzzyRank(searchPool, query, 30) : []), [isTitle, searchPool, query]);

  // Position poussee par le serveur, toujours a jour dans un ref.
  cpRef.current = clipProgress;

  // Remise a ZERO de la jauge + reamorcage de l'horloge locale a chaque nouvelle manche.
  useEffect(() => {
    setElapsed(0);
    localStartRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  }, [clipUrl]);

  // Jauge pilotee par le serveur : progression poussee (reset a 0 chaque manche)
  //  + horloge serveur en secours. PAS d'audio.currentTime (garderait la position
  //  de l'ancien extrait au demarrage du nouveau). Les 2 sources repartent de 0.
  useEffect(() => {
    if (reveal) { setElapsed(1); cancelAnimationFrame(rafRef.current); return; }
    const leadIn = (clipSync && typeof clipSync.leadInMs === 'number') ? clipSync.leadInMs : 2000;
    const localNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const loop = () => {
      let e = cpRef.current || 0;
      // Horloge serveur (source principale du fallback quand la sync est presente).
      if (startAt) {
        const clk = (srvNow() - startAt) / clipMs;
        if (clk > e) e = clk;
      }
      // Horloge locale de secours : jauge robuste meme si startAt/clipProgress manquent.
      const localClk = (localNow() - localStartRef.current - leadIn) / clipMs;
      if (localClk > e) e = localClk;
      setElapsed(Math.max(0, Math.min(1, e)));
      // preStart limite au lead-in reel : ne bloque jamais la jauge au-dela.
      if (startAt) setPreStart(srvNow() < startAt);
      else setPreStart((localNow() - localStartRef.current) < leadIn);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal, clipMs, startAt, clipSync]);

  const losePlayedRef = useRef(false);
  const iGuessed = !!proposals[myPseudo];
  const iWasCorrect = iGuessed && proposals[myPseudo]?.guess === correctAnswer;
  useEffect(() => {
    if (!reveal) { losePlayedRef.current = false; setNamesUp(false); return; }
    if (iGuessed && !iWasCorrect && !losePlayedRef.current) { losePlayedRef.current = true; playLose(); }
    const to = setTimeout(() => setNamesUp(true), 650);
    return () => clearTimeout(to);
  }, [reveal, iGuessed, iWasCorrect]);

  const tryPlay = () => {
    const el = audioRef.current;
    if (!el) return;
    try { el.currentTime = Math.max(0, (srvNow() - startAt) / 1000); } catch { /* ignore */ }
    el.play().then(() => setNeedGesture(false)).catch(() => setNeedGesture(true));
  };

  const canGuess = phase === 'thinking' && !hasGuessed && !preStart;
  const doGuess = (opt) => { if (canGuess) onGuess(opt); };

  const title = cleanTitle(mashupName);
  const author = mashupAuthor || '';
  const answerBig = isTitle ? title : author;
  const answerSub = isTitle ? `${t('mashupBy') || 'par'} ${author}` : title;
  const answerAvatarUrl = isTitle
    ? (mashupAuthor ? `/api/mashup/author-avatar?name=${encodeURIComponent(mashupAuthor)}` : null)
    : guessAvatars[correctAnswer];
  const playedPct = Math.max(0, Math.min(100, elapsed * 100));

  // Anti-collision : decale horizontalement les crans dont la fraction est trop
  // proche (plusieurs joueurs qui guess au meme instant), pour garder les
  // avatars lisibles. Meme helper reutilise par ChronoRound.
  const tickDodge = useMemo(() => computeTickDodge(guessTicks), [guessTicks]);

  return (
    <div className="mashup-round">
      <div className={`mashup-answer${reveal ? ' show' : ''}`}>
        {reveal && (
          <>
            <span className="mashup-answer-av"><AuthorAvatar url={answerAvatarUrl} label={answerBig} size={44} /></span>
            <div className="mashup-answer-txt">
              {answerBig && <span className="mashup-answer-title">{answerBig}</span>}
              {answerSub && <span className="mashup-answer-sub">{answerSub}</span>}
            </div>
          </>
        )}
      </div>

      <div className="mashup-bar" aria-hidden>
        <div className="mashup-bar-fill" style={{ width: `${playedPct}%` }} />
        {!reveal && <div className="mashup-playhead" style={{ left: `${playedPct}%` }} />}
        {guessTicks.map((g) => {
          const correct = proposals[g.pseudo]?.guess === correctAnswer;
          return (
            <div key={g.pseudo} className="mashup-tick" style={{ left: `${g.fraction * 100}%`, transform: `translateX(calc(-50% + ${tickDodge[g.pseudo] || 0}px))` }}>
              <div className={`mashup-tick-name${namesUp ? ' up' : ''}`}>{g.pseudo}</div>
              <div className={`mashup-tick-flip${reveal ? ' revealed' : ''}`}>
                <div className="mashup-tick-face front">
                  <Avatar url={g.avatarUrl} label={g.pseudo} className="mashup-tick-img" size={34} />
                </div>
                <div className={`mashup-tick-face back ${correct ? 'correct' : 'wrong'}`}>{correct ? '✓' : '✕'}</div>
              </div>
              <div className="mashup-tick-stem" />
            </div>
          );
        })}
      </div>

      <div className="mashup-volume">
        <span className="mashup-volume-ico" aria-hidden>🔊</span>
        <input type="range" min={0} max={1} step={0.01} value={volume}
          aria-label={t('volumeLabel') || 'Volume'}
          onChange={(e) => setVolume(parseFloat(e.target.value))} />
      </div>

      {preStart && !reveal && <div className="mashup-status">{t('mashupStarting') || 'Ca demarre...'}</div>}
      {needGesture && !preStart && !reveal && (
        <button type="button" className="btn mashup-play-btn" onClick={tryPlay}>▶ {t('listenAndGuess') || 'Ecouter'}</button>
      )}

      {phase === 'thinking' && isTitle && (() => {
        const attemptsLeft = Math.max(0, maxAttempts - attemptsUsed);
        const canSearch = canGuess && attemptsLeft > 0;
        const submit = (name) => {
          if (!canSearch) return;
          setAttemptsUsed((n) => n + 1);
          setQuery('');
          onGuess(name); // le dernier essai devient le cran (justesse cachee jusqu'au reveal)
        };
        return (
          <div className="mashup-guess-wrap">
            <div className="mashup-guess-hint">
              {t('mashupTitleHint') || 'Quel mashup ?'}
              <span className="bt-attempts">{Array.from({ length: maxAttempts }).map((_, i) => (
                <span key={i} className={`bt-dot${i < attemptsUsed ? ' used' : ''}`} aria-hidden>●</span>
              ))} {attemptsLeft}/{maxAttempts}</span>
            </div>
            {canSearch ? (
              <div className="bt-search">
                <input
                  type="text" className="bt-input" autoFocus
                  placeholder={t('mashupSearchPlaceholder') || 'Rechercher un mashup...'}
                  value={query} onChange={(e) => setQuery(e.target.value)}
                />
                <div className="bt-results">
                  {results.map((n) => (
                    <button key={n} type="button" className="bt-result" onClick={() => submit(n)}>{cleanTitle(n)}</button>
                  ))}
                  {!results.length && <div className="bt-empty">{t('mashupNoResult') || 'Aucun resultat'}</div>}
                </div>
              </div>
            ) : (
              <div className="mashup-status">{t('mashupWaitReveal') || 'En attente du reveal...'}</div>
            )}
          </div>
        );
      })()}

      {phase === 'thinking' && !isTitle && (
        <div className="mashup-guess-wrap">
          <div className="mashup-guess-hint">{t('chooseAuthorHint')}</div>
          <div className="mashup-guess-grid">
            {guessOptions.map((u) => {
              const display = guessNames[u] || u;
              return (
                <button
                  key={u}
                  type="button"
                  disabled={!canGuess}
                  onClick={() => doGuess(u)}
                  className={`guess-button mashup-guess-btn${hasGuessed && selectedGuess === u ? ' guess-selected' : ''}`}
                >
                  <AuthorAvatar url={guessAvatars[u]} label={display} size={40} />
                  <span className="mashup-guess-name">{display}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Confetti active={reveal && iWasCorrect} count={240} />
      <audio ref={audioRef} src={src || undefined} preload="auto" style={{ display: 'none' }} />
    </div>
  );
}
